import type { Theme } from '@material-ui/core'
import { makeStyles, Typography } from '@material-ui/core'
import React, { useState } from 'react'
import type { LeadFormSubmissionActivityDetails } from '@paintscout/api'
import { formatSubmissionAnswers } from '@paintscout/util/activity/lead-form/format-submission-answers'
import linkify from 'linkifyjs/html'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import classNames from 'classnames'
import { getLeadFormOptions } from '@paintscout/util/builder'
import Highlight from '../../Highlight'
import type { ActivityTypeComponentProps } from '../Activity'
import ActivityContent from '../ActivityContent'
import ActivityFooter from '../ActivityFooter'
import ActivityTitle from '../ActivityTitle'
import QuoteActivitySubtitle from '../QuoteActivitySubtitle'
import Collapse from '../../Collapse'
import HtmlContent from '../../HtmlContent'
import { IconButton } from '../../IconButton'
import { useClientOptions } from '../../../providers/ClientOptionsProvider'

const useStyles = makeStyles<Theme, ActivityTypeComponentProps<LeadFormSubmissionActivityDetails>>((theme: Theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingLeft: 0,
    paddingRight: 0,
    overflowWrap: 'break-word',
    paddingTop: 0,
    marginTop: theme.spacing(1)
  },
  bubble: {
    position: 'relative',
    marginRight: 0,
    width: 'fit-content',
    paddingRight: theme.spacing(3),
    '& a': {
      color: 'inherit'
    }
  },
  bubbleCorner: {
    position: 'absolute',
    top: 0,
    right: 0,
    borderStyle: 'solid',
    borderWidth: '8px',
    borderColor: `#fff #fff ${theme.palette.grey[400]} ${theme.palette.grey[400]}`,
    ['-moz-border-radius']: '0 0 0 5px',
    borderRadius: '0 0 0 5px',
    '@media print': {
      display: 'none'
    }
  },
  iconButton: {
    padding: theme.spacing(0.5),
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(2)
  },
  noteContent: {
    marginRight: theme.spacing(3)
  },
  files: {
    display: 'flex',
    flexDirection: 'column'
  }
}))

export default function LeadFormSubmissionActivity(
  props: ActivityTypeComponentProps<LeadFormSubmissionActivityDetails>
) {
  const { activity, showDocTypeDetails, showContent } = props
  const { options } = useClientOptions()
  const classes = useStyles(props)
  const [collapsed, setCollapsed] = useState(true)
  const leadForms = getLeadFormOptions({ options })
  return (
    <>
      <ActivityTitle>
        <Highlight>Lead form</Highlight> was <Highlight>submitted</Highlight>
      </ActivityTitle>
      {showContent && (
        <ActivityContent
          classes={{
            root: classes.content,
            bubble: classes.bubble
          }}
        >
          <div className={classes.bubbleCorner} />
          <IconButton
            size="small"
            onClick={() => {
              setCollapsed(!collapsed)
            }}
            className={classNames(classes.iconButton, 'no-redirect')}
          >
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
          <Collapse show={collapsed}>
            <Typography className={classes.noteContent}>
              {leadForms.find((form) => form.key === activity?.details.formKey)?.label}
            </Typography>
          </Collapse>
          <Collapse show={!collapsed}>
            <HtmlContent
              className={classes.noteContent}
              content={linkify(formatSubmissionAnswers(JSON.parse(activity.details?.note)))}
            />
          </Collapse>
        </ActivityContent>
      )}
      {activity.details?.uploadedFiles?.length > 0 && showContent && (
        <div className={classes.files}>
          {activity.details.uploadedFiles.map((file) => (
            <Typography variant="subtitle1" key={file.id} style={{ cursor: 'default' }}>
              {file.name}
            </Typography>
          ))}
        </div>
      )}
      <QuoteActivitySubtitle activity={activity} showDocTypeDetails={showDocTypeDetails} />
      <ActivityFooter timestamp={activity.details.timestamp} />
    </>
  )
}
